/**
 * 1.6.0 Administration → Integrations → AI assistants (MCP), for administrators (plan A5). Stream T's file (Admin.tsx
 * only mounts the tab when integrationsTabVisible says so; docs/dev/mcp-1.6.0-build.md):
 *
 *  - the downloads per OS from /downloads/mcp/latest.json and its manifest (version, size, SHA-256), as the storage
 *    agent's (AgentEnrollment.tsx; M serves the files);
 *  - install steps per OS from lib/mcp-install.ts (download, chmod and codesign on macOS, `prism-mcp setup`);
 *  - client snippets: Claude Desktop's claude_desktop_config.json, Claude Code's `claude mcp add prism -- …`, a
 *    generic stdio command; the update command;
 *  - a link to My account → AI assistant access to make a token, and the tokens in use (count, last used).
 */
import { Link } from 'react-router-dom';
import { Bot, KeyRound, ShieldCheck } from 'lucide-react';
import { useAssistantSettings, type Me } from '../../../api/hooks';
import { relative } from '../../../lib/format';
import { McpInstall } from './McpInstall';

/** Whether Administration shows the Integrations tab: administrators. */
export function integrationsTabVisible(me: Me): boolean {
  return me.permissions.includes('admin');
}

export function IntegrationsAdmin() {
  const settings = useAssistantSettings();
  const s = settings.data;
  return (
    <div className="page" data-testid="integrations">
      <h2>Integrations</h2>
      <p className="lede">Connect AI assistants such as Claude Desktop and Claude Code to PRISM with the PRISM MCP connector, a small program that runs on each person’s computer.
        It calls PRISM’s API over HTTPS with the person’s own assistant token, so the assistant sees and changes only what that person may, and nothing new is opened on PRISM.
        For a shared or service assistant, an <Link to="/admin/api-keys">API key</Link> works as its token too.</p>
      <section className="panel-card" style={{ marginBottom: 20 }} data-testid="integrations-assistants">
        <h3><Bot size={16} color="var(--accent-strong)" /> AI assistants (MCP)</h3>
        {s && (
          <div className="mcp-usage" data-testid="integrations-usage">
            {s.enabled
              ? <span className="status ready"><span className="dot" />Assistant access is on</span>
              : <span className="status failed"><span className="dot" />Assistant access is off</span>}
            <span className="muted">{s.liveTokens} token{s.liveTokens === 1 ? '' : 's'} in use{s.lastUsedAt ? ` · last used ${relative(s.lastUsedAt)}` : ''}</span>
            <Link className="btn sm ghost" to="/admin/access#ai-assistants"><ShieldCheck size={13} /> Access → AI assistants</Link>
          </div>
        )}
        {s && !s.enabled && <p className="muted" style={{ margin: 0 }}>Turn on “Allow AI assistant access” in Access → AI assistants first; until then every token is refused.</p>}
        <ol className="mcp-outline">
          <li><strong>Make a token.</strong> Each person makes their own in My account (their initials in the side bar) → <KeyRound size={12} /> AI assistant access, when their role may. It is shown once, with the setup command.</li>
          <li><strong>Install the connector</strong> on their computer and run the setup command (below).</li>
          <li><strong>Add it to the assistant</strong> with one of the snippets, then ask the assistant about PRISM.</li>
        </ol>
        <McpInstall />
      </section>
    </div>
  );
}
